import { ColorIdToColor } from "common/materials/library/colorIdToColor";
import { LayerConfigIdToJson } from "common/materials/library/layerConfigIdToJson";
import { MaterialTemplateIdToJson } from "common/materials/library/materialTemplateIdToJson";
import type { MaterialSrc } from "common/materials/materialSrc";
import { materialCacheKey } from "common/materials/materialSrc";
import { applyLayerConfig, parseDesignMaterialParams, parseLayerConfig } from "./materials/designMaterialParams";
import type { DesignMaterialParams } from "./materials/designMaterialTypes";
import THREEAssetLoader from "./THREEAssetLoader";

export class HTTPMaterialParameterLoader {
  private readonly designMaterialTemplateCache: { [key: string]: Promise<string> } = {};
  private readonly layerConfigCache: { [key: string]: Promise<string> } = {};
  private readonly designMaterialParamsCache: { [key: string]: Promise<DesignMaterialParams> } = {};

  private readonly assetLoader: THREEAssetLoader;

  private static _instance: HTTPMaterialParameterLoader | undefined;

  static get instance(): HTTPMaterialParameterLoader {
    return this._instance ?? (this._instance = new HTTPMaterialParameterLoader());
  }

  constructor() {
    this.assetLoader = THREEAssetLoader.instance;
  }

  private async fetchMaterialTemplate(templateId: number, forceRefresh: boolean): Promise<string> {
    if (!forceRefresh && this.designMaterialTemplateCache.hasOwnProperty(templateId)) {
      return this.designMaterialTemplateCache[templateId];
    }

    return (this.designMaterialTemplateCache[templateId] = new Promise<string>((resolve, reject) => {
      void (async () => {
        try {
          const templateJsonPath = MaterialTemplateIdToJson[templateId];
          const res = await this.assetLoader.fetchJson(templateJsonPath, forceRefresh);
          if (res) {
            resolve(res);
          } else {
            reject(null);
          }
        } catch (e) {
          console.error("Could not load design material parameters", templateId, e);
          reject(null);
        }
      })();
    }));
  }

  private async fetchLayerConfig(layerConfigId: number, forceRefresh: boolean): Promise<string> {
    if (!forceRefresh && this.layerConfigCache.hasOwnProperty(layerConfigId)) {
      return this.layerConfigCache[layerConfigId];
    }

    return (this.layerConfigCache[layerConfigId] = new Promise<string>((resolve, reject) => {
      void (async () => {
        try {
          const layerConfigJsonPath = LayerConfigIdToJson[layerConfigId];
          const res = await this.assetLoader.fetchJson(layerConfigJsonPath, forceRefresh);
          if (res) {
            resolve(res);
          } else {
            reject(null);
          }
        } catch (e) {
          console.error("Could not load layer config parameters", layerConfigId, e);
          reject(null);
        }
      })();
    }));
  }

  async fetchDesignMaterialParams(
    materialSrc: MaterialSrc,
    forceRefresh: boolean = false,
  ): Promise<DesignMaterialParams | null> {
    const cacheKey = materialCacheKey(materialSrc);
    if (materialSrc === null) {
      return null;
    }

    if (!forceRefresh && this.designMaterialParamsCache.hasOwnProperty(cacheKey)) {
      return this.designMaterialParamsCache[cacheKey];
    }

    return (this.designMaterialParamsCache[cacheKey] = new Promise<DesignMaterialParams>((resolve, reject) => {
      void (async () => {
        try {
          const res = await this.fetchMaterialTemplate(materialSrc.templateId, forceRefresh);
          const params = parseDesignMaterialParams(res);
          if (!params) {
            console.error("Could not parse design material parameters", materialSrc);
            reject(null);
            return;
          }

          if (materialSrc.layerConfigId !== undefined) {
            const layerRes = await this.fetchLayerConfig(materialSrc.layerConfigId, forceRefresh);
            const layerConfigParams = parseLayerConfig(layerRes);
            if (!layerConfigParams) {
              console.error("Could not parse design material layer config parameters", materialSrc);
              reject(null);
              return;
            }
            applyLayerConfig(params, layerConfigParams);
          }
          if (materialSrc.colorId !== undefined) {
            params.color = ColorIdToColor[materialSrc.colorId];
          }

          resolve(params);
        } catch (e) {
          console.error("Could not load design material parameters", materialSrc, e);
          reject(null);
        }
      })();
    }));
  }
}
